import React from 'react'
import {
  Button,
  ButtonColor,
  ButtonStyle,
  Icon,
  IconType,
  Slider,
  SliderProps,
} from '../../../../components'
import { Divider } from '../../../../components/Divider'
import {
  Collaboration,
  GetClipsSort,
  QueryGetClipsByCreatorIdArgs,
} from '../../../../gql/types'
import { useQuery } from '@apollo/client'
import {
  GET_CLIPS_BY_CREATOR_ID,
  GetClipsByCreatorResponse,
} from '../../../../gql'
import { ContentGrid } from '../../../../components/ContentGrid'
import { LIMIT_DEFAULT } from '../../../../utils/constants'

interface IProps extends Omit<SliderProps, 'children'> {
  collaboration: Collaboration
  onCreateClip: () => void
}

export const ViewClipsSlider: React.FC<IProps> = ({
  setOpen,
  open,
  collaboration,
  onCreateClip,
}) => {
  const limit = LIMIT_DEFAULT
  const { loading, data, fetchMore } = useQuery<
    GetClipsByCreatorResponse,
    QueryGetClipsByCreatorIdArgs
  >(GET_CLIPS_BY_CREATOR_ID, {
    variables: {
      creatorId: collaboration.creator.id,
      options: {
        query: '',
        filters: {
          campaignId: collaboration.campaign.id,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    },
    skip: !open,
  })

  const fetchData = () => {
    if (loading) {
      return
    }

    // if we have all the data, don't fetch more
    if (
      data?.getClipsByCreatorId?.items?.length >=
      data?.getClipsByCreatorId?.pagination?.numberOfHits
    ) {
      return
    }

    fetchMore({
      variables: {
        creatorId: collaboration.creator.id,
        options: {
          query: '',
          filters: {
            campaignId: collaboration.campaign.id,
          },
          sort: GetClipsSort.newest,
          limit,
          skip: data?.getClipsByCreatorId?.items?.length ?? 0,
        },
      },
    })
  }

  return (
    <Slider setOpen={setOpen} open={open} size='md'>
      <div className='flex h-full flex-col overflow-y-scroll bg-white shadow-xl'>
        {/* Header */}

        <div className='sm:sticky top-0 z-10 bg-white'>
          <div className='px-4 pt-6 sm:px-6'>
            <div className='flex items-start justify-between'>
              <h3 className='text-xl font-bold text-gray-900'>Clips</h3>
              <button
                type='button'
                className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none'
                onClick={() => setOpen(false)}
              >
                <span className='sr-only'>Close panel</span>
                <Icon
                  type={IconType.x}
                  className='h-6 w-6'
                  aria-hidden='true'
                />
              </button>
            </div>
            {collaboration.canUploadClip && (
              <div className='mt-6 flex flex-wrap font-bold'>
                <Button
                  title='Upload Clip'
                  type='button'
                  styleType={ButtonStyle.fill}
                  colour={ButtonColor.primary}
                  className='flex-1 font-bold'
                  onClick={() => onCreateClip()}
                />
              </div>
            )}
          </div>
          <Divider className='py-6' />
        </div>

        {/* Main */}
        <div className='pb-1 sm:pb-6 px-4 sm:px-6 overflow-y-scroll'>
          <ContentGrid
            clips={data?.getClipsByCreatorId?.items || []}
            hasMore={
              data?.getClipsByCreatorId?.items?.length <
              data?.getClipsByCreatorId?.pagination?.numberOfHits
            }
            fetchData={fetchData}
            loading={loading}
            allowDownload={true}
            type='creator'
            useWindow={false}
          />
        </div>
      </div>
    </Slider>
  )
}
